import {ExplorerDatasetConfig, ExplorerDatasetType} from './explorer-mrt-types';
import {ResourceType} from '../../../../@types/resource-type';
import authService from '../../../../services/auth.service';

const USE_OKTA = process.env.REACT_APP_USE_OKTA === 'true';

/**
 * Configuration for dataset dropdown in Explorer
 * Controls order, labels, enabled state, and default selection
 */
export const datasetConfigs: ExplorerDatasetConfig[] = [
    {
        value: ResourceType.SAFETY_MODELS,
        label: 'Safety Models',
        enabled: true,
        order: 1,
    },
    {
        value: ResourceType.COMPONENT_CATALOGUE,
        label: 'Component Catalogue',
        enabled: true,
        order: 2,
        // Components are created in the working area that will own them, never from the
        // catalogue itself (component-solution-spec.md section 5, following the Safety
        // Recommendations precedent).
        creatable: false,
    },
    {
        value: ResourceType.NEW_OCCURRENCE_REPORTS,
        label: 'Occurrence Reports',
        enabled: true,
        order: 3,
    },
    {
        value: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
        label: 'Occurrence Report Descriptors',
        enabled: true,
        order: 4,
    },
    {
        value: ResourceType.INTEGRATED_RISK_PICTURES,
        label: 'Integrated Risk Pictures',
        enabled: true,
        order: 5,
    },
    {
        value: ResourceType.SAFETY_RECOMMENDATIONS,
        label: 'Safety Recommendations',
        enabled: true,
        order: 6,
        // Recommendations arrive from a source connector's sync, never from a person.
        creatable: false,
    },
    {
        value: ResourceType.GROUPS,
        label: 'Working Areas',
        enabled: true,
        order: 7,
    },
    {
        value: ResourceType.USERS,
        label: 'Users',
        enabled: true,
        order: 8,
    },
    {
        value: ResourceType.SERVICE_USER_ACCOUNTS,
        label: 'Service Users',
        enabled: true,
        order: 9,
    },
    {
        value: ResourceType.WORKSHOPS,
        label: 'Workshops',
        enabled: false, // Disabled by default
        order: 10,
    },
    {
        value: ResourceType.RISK_FORECASTS,
        label: 'Risk Forecasts',
        enabled: false, // Disabled by default
        order: 11,
    },
    {
        value: ResourceType.OPERATIONAL_CHANGES,
        label: 'Operational Changes',
        enabled: false, // Disabled by default
        order: 12,
    },
    {
        value: ResourceType.PERFORMANCE_SHAPING_FACTORS,
        label: 'PSF Templates',
        enabled: false,
        order: 13,
    },
    {
        value: ResourceType.TRAFFIC_MODELS,
        label: 'Traffic Models',
        enabled: false, // Disabled by default
        order: 14,
    },
    {
        value: ResourceType.FOUNDRY_EVENTS,
        label: 'Foundry Events',
        enabled: true,
        order: 15,
    }
];

// Default dataset when Explorer loads
export const defaultDataset: ExplorerDatasetType = ResourceType.SAFETY_MODELS;

// Get sorted and enabled datasets (controls the order in the dropdown)
export const getEnabledDatasets = (): ExplorerDatasetConfig[] => {
    return datasetConfigs
        .filter(config => {
            // Check if dataset is enabled
            if (!config.enabled) {
                return false;
            }
            
            // Users, Service User Accounts, and Foundry Events are only available to super users
            if (config.value === ResourceType.USERS ||
                config.value === ResourceType.SERVICE_USER_ACCOUNTS) {
                return authService.isSuperUser();
            }

            // Foundry Events require Okta and super user
            if (config.value === ResourceType.FOUNDRY_EVENTS) {
                return USE_OKTA && authService.isSuperUser();
            }
            
            return true;
        })
        .sort((a, b) => a.order - b.order);
};

/** Whether a dataset offers Create at all. Unset means yes, which is true of all but a few. */
export const isDatasetCreatable = (value: ExplorerDatasetType): boolean =>
    datasetConfigs.find(config => config.value === value)?.creatable !== false;

// Get dataset config by value (e.g. ResourceType.SAFETY_MODELS)
export const getDatasetConfig = (value: ExplorerDatasetType): ExplorerDatasetConfig | undefined => {
    return datasetConfigs.find(config => config.value === value);
};